import { AutocompleteWithTags } from 'components/common/simpleInputFields/autocompleteWithTags';
import { MemoAutocompleteWithText } from 'components/common/simpleInputFields/autocompleteWithText';
import { MemoCheckboxInput } from 'components/common/simpleInputFields/checkbox';
import { MemoSimpleCheckboxGroup } from 'components/common/simpleInputFields/checkboxGroup';
import { MemoSimpleDatepicker } from 'components/common/simpleInputFields/datepicker';
import { MemoMultiChoice } from 'components/common/simpleInputFields/multichoice';
import { MemoPhoneInput } from 'components/common/simpleInputFields/phoneInput';
import { MemoSingleChoice } from 'components/common/simpleInputFields/singleChoice';
import { MemoSimpleTextField } from 'components/common/simpleInputFields/textField';
import StatefulRichTextFieldComponent from 'components/common/RichTextEditor/StatefulWrapper';
import {
  useEmailInput,
  withEmailInput,
} from 'components/common/withEmailInput';
import { ReactElement, memo, useEffect } from 'react';
import { MemoStatefulFormInputComponent } from './stateful';
import { StatefulProps } from './types';

export function StatefulMultiselect({
  formKey,
  ...props
}: StatefulProps): ReactElement {
  return (
    <MemoStatefulFormInputComponent
      {...props}
      formKey={formKey}
      Component={MemoMultiChoice}
    />
  );
}

export const MemoStatefulMultiselect = memo(StatefulMultiselect);

export function StatefulSingleSelect({
  formKey,
  ...props
}: StatefulProps): ReactElement {
  return (
    <MemoStatefulFormInputComponent
      {...props}
      formKey={formKey}
      Component={MemoSingleChoice}
    />
  );
}

export const MemoStatefulSingleSelect = memo(StatefulSingleSelect);

export function StatefulDatepicker({
  formKey,
  ...props
}: StatefulProps): ReactElement {
  return (
    <MemoStatefulFormInputComponent
      {...props}
      formKey={formKey}
      Component={MemoSimpleDatepicker}
    />
  );
}

export const MemoStatefulDatepicker = memo(StatefulDatepicker);

export function StatefulTextField({
  formKey,
  ...props
}: StatefulProps): ReactElement {
  return (
    <MemoStatefulFormInputComponent
      {...props}
      formKey={formKey}
      Component={MemoSimpleTextField}
      debounce={props.debounce ?? true}
    />
  );
}

export const MemoStatefulTextField = memo(StatefulTextField);

export function StatefulAutocompleteWithText({
  formKey,
  ...props
}: StatefulProps): ReactElement {
  return (
    <MemoStatefulFormInputComponent
      {...props}
      formKey={formKey}
      Component={MemoAutocompleteWithText}
      debounce={props.debounce ?? true}
    />
  );
}

export const MemoStatefulAutocompleteWithText = memo(
  StatefulAutocompleteWithText
);

function _StatefulEmailInput({
  formKey,
  ...props
}: StatefulProps): ReactElement {
  const emailInputContext = useEmailInput();
  useEffect(() => {
    emailInputContext.setFormKey(formKey);
  }, [formKey, emailInputContext]);

  return (
    <MemoStatefulFormInputComponent
      {...emailInputContext}
      {...props}
      Component={AutocompleteWithTags}
      formKey={formKey}
    />
  );
}

export const StatefulEmailInput = withEmailInput(_StatefulEmailInput);

export function StatefulCheckboxGroup({
  formKey,
  ...props
}: StatefulProps): ReactElement {
  return (
    <MemoStatefulFormInputComponent
      {...props}
      Component={MemoSimpleCheckboxGroup}
      formKey={formKey}
    />
  );
}

export function StatefulCheckboxInput({
  formKey,
  ...props
}: StatefulProps): ReactElement {
  return (
    <MemoStatefulFormInputComponent
      {...props}
      Component={MemoCheckboxInput}
      formKey={formKey}
    />
  );
}

export function StatefulPhoneInput({
  formKey,
  ...props
}: StatefulProps): ReactElement {
  return (
    <MemoStatefulFormInputComponent
      {...props}
      Component={MemoPhoneInput}
      formKey={formKey}
    />
  );
}

export const MemoStatefulPhoneField = memo(StatefulPhoneInput);

export function StatefulRichTextField({
  formKey,
  ...props
}: StatefulProps): ReactElement {
  return (
    <MemoStatefulFormInputComponent
      {...props}
      formKey={formKey}
      Component={StatefulRichTextFieldComponent}
      debounce={props.debounce ?? true}
    />
  );
}

export const MemoStatefulRichTextField = memo(StatefulRichTextField);
